import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useSlate } from "slate-react";
import { Editor, Node, Element, Range, Text } from "slate";
import { Button, Modal } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classes from "./AddEditLinkModal.scss";
import { insertOrUpdateLink } from "../InlineEditorUtils";
import { ELEMENT_TYPE_LINK } from "../InlineEditorConstants";
import { isURL } from "Utils";
import { FormTextInput } from "UIComponents/InputTextField";
import { SHIFT_ENTER_SUBMIT_SHORTCUT } from "Constants/stringConstants";

const LINK_CONSTANTS = {
  URL: "url",
  DISPLAY_TEXT: "displayText",
};

const AddEditLinkModal = props => {
  const { onClose, linkElement } = props;
  const { t } = useI18n(["common"]);

  const editor = useSlate();

  const textInputRef = useRef(null);
  const urlInputRef = useRef(null);

  const [displayText, setDisplayText] = useState("");
  const [url, setUrl] = useState("");
  const [mode, setMode] = useState("create");

  const isSaveButtonDisabled = _.isEmpty(_.trim(displayText)) || _.isEmpty(url);

  const setDataForCreateMode = () => {
    const { selection } = editor;
    const fragment = Editor.fragment(editor, selection);
    const selectedText = fragment.map(node => Node.string(node)).join("");
    setDisplayText(selectedText);
    if (isURL(selectedText)) {
      setUrl(selectedText);
    } else setUrl("");
    setMode("create");
  };

  useEffect(() => {
    // if linkElement is passed, it means the link text is clicked and modal is opened for editing
    if (linkElement) {
      setUrl(linkElement.url);
      setMode("edit");
      setDisplayText(Node.string(linkElement));
    } else {
      const { selection } = editor;
      if (selection && !Range.isCollapsed(selection)) {
        // Collect only inline nodes (e.g., links) within the selection
        const inlineNodes = Array.from(
          Editor.nodes(editor, {
            at: selection,
            match: n =>
              Text.isText(n) || // Include text nodes
              (Element.isElement(n) && !Editor.isBlock(editor, n)), // Include inline elements
          })
        );

        const hasLinkElement = inlineNodes.some(
          ([node]) => Element.isElement(node) && node.type === ELEMENT_TYPE_LINK
        );

        const hasNonLinkElement = inlineNodes.some(([node, path]) => {
          if (Text.isText(node)) {
            // Check if the parent of the text node is a link element
            const [parent] = Editor.parent(editor, path);
            return !(
              Element.isElement(parent) && parent.type === ELEMENT_TYPE_LINK
            );
          }
          // Check for inline elements that aren't links
          return Element.isElement(node) && node.type !== ELEMENT_TYPE_LINK;
        });

        const hasMixedElements = hasLinkElement && hasNonLinkElement;
        if (hasMixedElements) {
          setDataForCreateMode();
        } else {
          // Check if the selection is inside a link
          const [linkNode] = Editor.nodes(editor, {
            match: n =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              n.type === ELEMENT_TYPE_LINK,
          });

          if (linkNode) {
            const linkElement = linkNode[0];
            setUrl(linkElement.url);
            setMode("edit");
            setDisplayText(Node.string(linkElement));
          } else {
            setDataForCreateMode();
          }
        }
      }
    }
    if (textInputRef.current) textInputRef.current.focus();
  }, [editor]);

  const handleSave = () => {
    if (!urlInputRef.current.isValid()) {
      insertOrUpdateLink({
        editor,
        url,
        displayText: _.trim(displayText),
        linkElement,
      });
      onClose();
    }
  };

  const handleInputChange = params => {
    const key = Object.keys(params)[0];
    const valueData = params[key];
    switch (key) {
      case LINK_CONSTANTS.URL: {
        setUrl(valueData);
        break;
      }
      case LINK_CONSTANTS.DISPLAY_TEXT: {
        setDisplayText(valueData);
        break;
      }
    }
  };

  const validateUrl = val => {
    const isValid = isURL(val);
    if (!isValid) {
      return t("common:invalid_link");
    }
    return "";
  };

  return (
    <Modal
      width={"480px"}
      height={"fit-content"}
      shouldCloseOnEsc={true}
      onClose={onClose}
      dsVersion={"2.0"}
    >
      <Modal.Header isClosable={true}>
        {mode === "create" ? t("common:add_link") : t("common:edit_link")}
      </Modal.Header>
      <Modal.Body>
        <div className={classes.bodyContainer}>
          <div className={classes.inputContainer}>
            <FormTextInput
              size="large"
              name={"displayText"}
              textInputLabel={t("common:display_text")}
              value={displayText}
              updateInputField={handleInputChange}
              testId="inlineEditor-linkModal-displayText"
              dsVersion={"2.0"}
              placeholder={t("common:typeHere")}
              autoFocus={true}
            />
          </div>
          <div className={classes.inputContainer}>
            <FormTextInput
              size="large"
              name={"url"}
              label={t("common:link")}
              value={url}
              updateInputField={handleInputChange}
              testId="inlineEditor-linkModal-url"
              dsVersion={"2.0"}
              placeholder={t("common:typeHere")}
              customValidation={validateUrl}
              ref={urlInputRef}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footerContainer}>
          <Button
            variant="neutral"
            type="outlined"
            dsVersion="2.0"
            size="medium"
            onClick={onClose}
          >
            {t("common:cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            dsVersion="2.0"
            size="medium"
            disabled={isSaveButtonDisabled}
            shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
          >
            {t("common:save")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditLinkModal;
